/**
 * `/register`
 *
 * Structure transcribed from the mockup's `isAuth` block with `authRegister` true — the Name field
 * appears above Email, and the password carries a hint.
 *
 * Field-level errors come from the envelope's `fields` map, so the same validation the server
 * enforces is what the user sees. The client never invents a rule the server does not have, which
 * is how the two drift apart and produce "it looked fine but was rejected".
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { AuthCard, AuthLayout } from '../../components/composites/AuthLayout';
import { Button, Field, Notice } from '../../components/primitives';
import { ApiError, register } from './api';
import { ME_QUERY_KEY, useAuth } from './useAuth';

export function RegisterScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const { account, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && account) {
      const target =
        search.redirect && search.redirect.startsWith('/') ? search.redirect : '/liturgies';
      navigate({ to: target as never, replace: true });
    }
  }, [account, isLoading, search.redirect, navigate]);

  const mutation = useMutation({
    mutationFn: () => register({ email, password, display_name: displayName }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      const target =
        search.redirect && search.redirect.startsWith('/') ? search.redirect : '/liturgies';
      await navigate({ to: target as never, replace: true });
    },
  });

  const error = mutation.error instanceof ApiError ? mutation.error : undefined;
  const fields = error?.fields ?? {};

  // Only a form-level notice when the failure is not attributable to a field; otherwise the same
  // message would appear twice.
  const showFormNotice = error && Object.keys(fields).length === 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <AuthLayout
      eyebrow="PIUS SELECTIONS"
      heading={t('auth.register.title')}
      blurb={t('auth.register.lede')}
    >
      {showFormNotice ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <form onSubmit={submit} noValidate>
        <AuthCard>
          <Field
            label={t('auth.register.name')}
            name="display_name"
            placeholder="Lorraine Toal"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={fields['display_name'] ? t('auth.register.name_required') : undefined}
            autoComplete="name"
            required
          />

          <Field
            label={t('auth.register.email')}
            type="email"
            name="email"
            placeholder="name@parish.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fields['email'] ? error?.message : undefined}
            autoComplete="email"
            required
          />

          <Field
            label={t('auth.register.password')}
            type="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={fields['password'] ? error?.message : undefined}
            hint={t('auth.register.password_hint')}
            autoComplete="new-password"
            required
          />

          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.register.submitting') : t('auth.register.submit')}
          </Button>
        </AuthCard>
      </form>

      <p className="auth-alt">
        {t('auth.register.have_account')}{' '}
        <Link to="/login" search={search.redirect ? { redirect: search.redirect } : {}}>
          {t('auth.register.signin_link')}
        </Link>
      </p>
    </AuthLayout>
  );
}
